import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentStatus({ mode }) {
  const [status, setStatus] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (mode !== "success") return;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.status === "failed") { setStatus("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (tries >= 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode]);

  const map = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment…", sub: "Hang tight, this only takes a moment.", color: "text-volt" },
    paid: { icon: CheckCircle2, title: "Payment confirmed", sub: info ? `Your ${info.label} order is in. The Etoire team will reach out shortly.` : "Thank you! Your order is in.", color: "text-volt" },
    failed: { icon: XCircle, title: "Payment not completed", sub: "Your card wasn't charged. You can try again anytime.", color: "text-destructive" },
    timeout: { icon: Clock, title: "Still processing", sub: "This is taking longer than usual — if you were charged, your order is safe and we'll confirm it.", color: "text-volt" },
    cancelled: { icon: XCircle, title: "Checkout cancelled", sub: "No charge was made. Your order wasn't placed.", color: "text-muted-foreground" },
    error: { icon: XCircle, title: "Something went wrong", sub: "We couldn't find your session. Please start a new order.", color: "text-destructive" },
  };
  const s = map[status] || map.checking;
  const Icon = s.icon;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div data-testid={`payment-${status}`} className="relative text-center max-w-md">
        <Icon className={`w-16 h-16 mx-auto ${s.color} ${s.spin ? "animate-spin" : ""}`} />
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mt-6">{s.title}</h1>
        <p className="text-muted-foreground mt-3">{s.sub}</p>
        <Link to="/#orders" className="inline-block mt-8 bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-3.5 hover:brightness-110 transition-all duration-200">Back to Etoire</Link>
      </div>
    </div>
  );
}
